import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { useNavigate, useLocation, useNavigationType } from "react-router-dom";
import { getItemsPerCategory, UpdateProduct } from "../../../server/Product.js";
import {
  CategoryUpdate,
  CategoryAdd,
  CategoryDelete,
  getAllCategory,
  toggleExpandCardAction,
  resetExpandedCards,
} from "../../../server/Category";
import {
  FiEdit2,
  FiCheckSquare,
  FiSquare,
  FiSave,
  FiPlus,
  FiTrash,
} from "react-icons/fi";
import "./showCategory.scss";
import AddCategoryDialog from "./addCategoryDialog";
import {
  GripVertical,
  Layers,
  PlusCircle,
  Upload,
  Eye,
  EyeOff,
  PackageSearch,
} from "lucide-react";
import DeleteCategoryDialog from "./deleteCategoryDialog";
import { sortCategoryId } from "../../../../axiosInstance.js";
import MoveCategoryDialog from "./MoveCategoryDialog.js";
import ReactTooltip from "react-tooltip";
import showAlert, {
  showAlertWithConfirmCancel,
} from "../../../server/ShowAlert";
import { childBelongsToParent, isInSearchVisible } from "./categorySearchUtils";

const ShowCategory = ({
  parentId = "ROOT",
  isNested = false,
  level = 0,
  categoriesState: externalCategories = null,
  isMainSection = true,
  searchVisibleIds = null,
}) => {
  const getId = (item) => item?.Id ?? item?.id;
  const getParentId = (item) => item?.parentId ?? item?.FatherId ?? "ROOT";
  const getName = (item) => item?.name ?? item?.Name ?? "";
  const isFamilyCategory = (item) => Boolean(item?.isFamily);
  const showsCategoryHeader = (item) => item?.showHeader !== false;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();

  const allCategoriesState = useSelector((state) => state.category.categories);
  const stationId = useSelector((state) => state.user.stationId);
  const expandedCards = useSelector((state) => state.category.expandedCards);

  const [currentItems, setCurrentItems] = useState([]);
  const [addingDialogOpen, setAddingDialogOpen] = useState(false);
  const [fatherIdAddCategory, setFatherIdAddCategory] = useState(null);
  const [fatherSlugAddCategory, setFatherSlugAddCategory] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [moveCategoryOpen, setMoveCategoryOpen] = useState(null);
  const [renamingCardId, setRenamingCardId] = useState(null);
  const [renameInputValue, setRenameInputValue] = useState("");

  useEffect(() => {
    let filteredItems;
    const parentIdAsString = String(parentId);
    const sourceArray = externalCategories || allCategoriesState;

    if (isNested && Array.isArray(externalCategories)) {
      filteredItems = [...externalCategories];
    } else {
      filteredItems = Array.isArray(sourceArray)
        ? sourceArray.filter(
            (item) => String(getParentId(item)) === parentIdAsString,
          )
        : [];
    }

    if (searchVisibleIds?.size && isMainSection) {
      filteredItems = filteredItems.filter((item) =>
        isInSearchVisible(searchVisibleIds, item),
      );
    }

    filteredItems.sort((a, b) => (a.OrderNumber || 0) - (b.OrderNumber || 0));
    setCurrentItems(filteredItems);
  }, [
    allCategoriesState,
    externalCategories,
    parentId,
    level,
    searchVisibleIds,
    isMainSection,
    isNested,
  ]);

  useEffect(() => {
    if (stationId) {
      dispatch(getAllCategory(stationId));
    }
  }, [stationId, dispatch]);

  useEffect(() => {
    if (level === 0 && navigationType === "PUSH") {
      dispatch(resetExpandedCards());
    }
  }, [location.pathname, navigationType, dispatch, level]);

  useEffect(() => {
    const frameId = requestAnimationFrame(() => {
      ReactTooltip.rebuild();
    });
    return () => cancelAnimationFrame(frameId);
  }, [currentItems, expandedCards]);

  const toggleExpandCard = (cardId) => {
    dispatch(toggleExpandCardAction(cardId));
  };

  const updateCategory = async ({ id, title, value, slug }) => {
    const res = await dispatch(
      CategoryUpdate(
        {
          Mkt: id,
          Title: title,
          value: value,
          slug: slug,
        },
        stationId,
      ),
    );
    if (res.ok)
      showAlert({
        msgTitle: "הקטגוריה עודכנה בהצלחה",
        msgType: "success",
      });
    else
      showAlert({
        msgTitle: "עדכון הקטגוריה נכשל",
        msgType: "error",
      });
  };

  const addNewCategory = async (
    newCategoryName,
    isFamily,
    showHeader = true,
  ) => {
    if (newCategoryName.trim() === "") return;
    const isRootAdd = fatherIdAddCategory === 0;
    const data = {
      parentId: fatherIdAddCategory?.toString() || "ROOT",
      name: newCategoryName,
      branch: stationId,
      isFamily: isRootAdd ? false : isFamily,
      status: "ACTIVE",
      parentSlug: fatherSlugAddCategory || "_root",
      ...(showHeader === false && { showHeader: false }),
    };
    const createSlug = (slug) => {
      return slug
        .normalize("NFKC")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9-]/g, "-")
        .replace(/-+/g, "-")
        .replace(/^-|-$/g, "");
    };

    try {
      const response = await fetch(
        `https://api.mymemory.translated.net/get?q=${encodeURIComponent(newCategoryName)}&langpair=he|en`,
      );
      const result = await response.json();
      const translatedText =
        result.responseData?.translatedText || newCategoryName;
      const tempSlug = createSlug(translatedText);
      dispatch(CategoryAdd(tempSlug, data, stationId));
    } catch (error) {
      console.error("Error translating category name:", error);
      const slug = createSlug(newCategoryName);
      dispatch(CategoryAdd(slug, data, stationId));
    }

    setAddingDialogOpen(false);
  };

  const [updatingItems, setUpdatingItems] = useState({});

  const onDragEnd = async (result) => {
    const { source, destination } = result;
    if (!destination) return;
    if (source.droppableId !== destination.droppableId) return;
    if (source.index === destination.index) return;
    const originalItems = [...currentItems];
    const reorderedItems = [...currentItems];
    const [movedItem] = reorderedItems.splice(source.index, 1);
    reorderedItems.splice(destination.index, 0, movedItem);
    const movedItemId = getId(movedItem);
    if (movedItemId == null) return;
    if (updatingItems[movedItemId]) return;

    const positionPayload = (() => {
      const total = reorderedItems.length;
      const idx = destination.index;
      if (idx === 0) return { kind: "prepend" };
      if (idx === total - 1) return { kind: "append" };
      const beforeItem = reorderedItems[idx - 1];
      const afterItem = reorderedItems[idx + 1];
      return {
        kind: "betweenByRank",
        before: beforeItem.rank ?? beforeItem.id,
        after: afterItem.rank ?? afterItem.id,
      };
    })();

    showAlertWithConfirmCancel({
      msgTitle: "האם לעדכן את סדר הקטגוריות?",
      confirmText: "אישור",
      cancelText: "ביטול",
      onConfirm: async () => {
        setCurrentItems(reorderedItems);
        setUpdatingItems((prev) => ({ ...prev, [movedItemId]: true }));

        try {
          const res = await dispatch(
            CategoryUpdate(
              {
                slug: movedItem.slug,
                Title: "position",
                value: positionPayload,
                Mkt: movedItemId,
                rank: movedItem.rank,
              },
              stationId,
            ),
          );
          if (res?.isAxiosError || res instanceof Error) {
            throw res;
          }
        } catch (error) {
          console.error("Failed to update category position:", error);
          setCurrentItems(originalItems);
        } finally {
          setUpdatingItems((prev) => ({ ...prev, [movedItemId]: false }));
        }
      },
    });
  };

  const handleCardClick = (id, childrenCount, slug) => {
    if (childrenCount === 0) {
      navigate(`/products-edit/${slug}`);
    }
    toggleExpandCard(id);
  };

  const isSure = () => {
    return new Promise((resolve) => {
      showAlertWithConfirmCancel({
        msgTitle: "האם אתה בטוח שברצונך לשנות את סטטוס הקטגוריה?",
        msgText: "",
        confirmText: "כן",
        cancelText: "לא",
        onConfirm: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
  };

  const toggleActiveStatus = async (card) => {
    const confirmed = await isSure();

    if (!confirmed) return;

    const newStatusValue = card.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";

    updateCategory({
      id: getId(card),
      title: "status",
      value: newStatusValue,
      slug: card.slug,
    });

    if (card.status === "ACTIVE") {
      try {
        const res = await getItemsPerCategory(getId(card), stationId);

        res.data.forEach((prod) =>
          UpdateProduct({
            Mkt: prod.Mkt,
            StationId: stationId,
            Title: "DisableProductId",
            value: "5",
          }),
        );
      } catch (e) {
        console.error("Error disabling products:", e);
      }
    }
  };

  const toggleShowHeader = (card) => {
    updateCategory({
      id: getId(card),
      title: "showHeader",
      value: !showsCategoryHeader(card),
      slug: card.slug,
    });
  };

  const startRenamingCard = (card) => {
    setRenamingCardId(getId(card));
    setRenameInputValue(getName(card));
  };

  const saveRenamingCard = (card) => {
    updateCategory({
      id: getId(card),
      title: "name",
      value: renameInputValue,
      slug: card.slug,
    });
    setRenamingCardId(null);
  };

  const openAddingDialog = (id, slug) => {
    setFatherIdAddCategory(id);
    setFatherSlugAddCategory(slug);
    setAddingDialogOpen(true);
  };
  useEffect(() => {}, [fatherIdAddCategory]);
  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };

  const deleteEmptyCategory = async (categorySlug) => {
    try {
      await dispatch(CategoryDelete(categorySlug, stationId));
      showAlert({
        msgTitle: "הקטגוריה נמחקה בהצלחה",
        msgType: "success",
      });
    } catch {
      showAlert({
        msgTitle: "מחיקת הקטגוריה נכשלה",
        msgType: "error",
      });
    }
  };

  const handleDelete = (item) => {
    console.log("item", item);
    getItemsPerCategory(item.slug, stationId)
      .then((x) => {
        if (x.data.count === 0) {
          deleteEmptyCategory(item.slug);
        } else {
          setCategoryToDelete(item.slug);
          setDeleteDialogOpen(true);
        }
      })
      .catch((e) => console.error("Error checking products before delete:", e));
  };

  const deleteCtegoryWithProducts = () => {
    setDeleteDialogOpen(false);
    getItemsPerCategory(categoryToDelete, stationId)
      .then((x) => {
        const products = x.data;
        products.forEach((prod) => {
          UpdateProduct({
            Mkt: prod.Mkt,
            Title: "categoryId",
            value: sortCategoryId,
            StationId: stationId,
          });
        });
        return deleteEmptyCategory(categoryToDelete);
      })
      .catch((e) => console.error("Error moving products before delete:", e));
  };

  const handleMoving = (categoryId) => {
    setMoveCategoryOpen(() => categoryId);
  };

  const totalCounts = currentItems.reduce(
    (acc, item) => ({
      admin: acc.admin + (item.totalProducts || 0),
      customer: acc.customer + (item.CustomerProductCount || 0),
      active: acc.active + (item.activeProducts || 0),
    }),
    { admin: 0, customer: 0, active: 0 },
  );

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId={isNested ? `nested-list-${level}` : "list"}>
        {(provided) => (
          <div
            className={`categories-container ${isNested ? "nested-categories" : ""}`}
            {...provided.droppableProps}
            ref={provided.innerRef}
          >
            {level === 0 && isMainSection && (
              <div className="category-summary">
                <span className="summary-title">סה"כ מוצרים:</span>
                <div className="summary-values">
                  {/*         <span>כל המוצרים: {totalCounts.admin}</span>
                  <span>פעילים: {totalCounts.active}</span>
                  <span>לקוח: {totalCounts.customer}</span> */}
                  <span>כל המוצרים: {totalCounts.admin}</span>
                  <span>פעילים: {totalCounts.active}</span>
                </div>
              </div>
            )}
            {level === 0 && isMainSection && (
              <div
                className="category-item add-category-item"
                onClick={() => openAddingDialog(0, 0)}
              >
                <div className="add-category-content">
                  <div className="circle-icon">
                    <PlusCircle className="plus-icon" />
                  </div>
                  <p>הוסף מחלקה חדשה</p>
                </div>
              </div>
            )}
            {currentItems.map((item, index) => {
              let children = Array.isArray(item.children)
                ? item.children
                : allCategoriesState.filter((child) =>
                    childBelongsToParent(child, item),
                  );

              if (searchVisibleIds?.size && isMainSection) {
                children = children.filter((child) =>
                  isInSearchVisible(searchVisibleIds, child),
                );
              }
              return (
                <Draggable
                  key={getId(item)?.toString()}
                  draggableId={getId(item)?.toString()}
                  index={index}
                  isDragDisabled={Boolean(updatingItems[getId(item)])}
                >
                  {(provided) => (
                    <div
                      className={`category-item${isFamilyCategory(item) ? " category-item--family" : ""}`}
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                    >
                      <div
                        className={`category-content level-${isFamilyCategory(item) ? "family" : level}`}
                      >
                        <div className="category-content-main">
                          <span
                            className="category-drag-handle"
                            {...provided.dragHandleProps}
                            aria-label="גרור לשינוי סדר"
                            title="גרור לשינוי סדר"
                          >
                            <GripVertical size={18} strokeWidth={2} />
                          </span>
                          {renamingCardId === getId(item) ? (
                            <form
                              className="rename-form"
                              onSubmit={(e) => {
                                e.preventDefault();
                                saveRenamingCard(item);
                              }}
                            >
                              <input
                                type="text"
                                value={renameInputValue}
                                onChange={(e) =>
                                  setRenameInputValue(e.target.value)
                                }
                                className="rename-input"
                                autoFocus
                                aria-label="Rename category"
                              />
                              <button
                                type="submit"
                                className="icon-button"
                                aria-label="Save new name"
                              >
                                <FiSave size={20} />
                              </button>
                            </form>
                          ) : (
                            <span
                              className="category-name"
                              onClick={() =>
                                handleCardClick(
                                  getId(item),
                                  children.length,
                                  item.slug,
                                )
                              }
                            >
                              {isFamilyCategory(item) && (
                                <span className="family-badge">
                                  <Layers size={14} aria-hidden="true" />
                                  מקבץ
                                </span>
                              )}
                              <span className="category-name-text">
                                {getName(item)}
                              </span>
                              <span className="category-counts">
                                <span>
                                  כל המוצרים : {item.totalProducts ?? 0}
                                </span>
                                |<span>פעילים: {item.activeProducts ?? 0}</span>
                                {/* |
                                <span>
                                  לקוח: {item.CustomerProductCount ?? 0}
                                </span> */}
                              </span>
                            </span>
                          )}
                        </div>
                        {item.Id !== sortCategoryId && (
                          <span
                            style={{ display: "flex", alignItems: "center" }}
                          >
                            <button
                              type="button"
                              data-tip
                              data-for="viewCategoryProducts"
                              onClick={() =>
                                navigate(`/products-edit/${item.slug}`)
                              }
                              className="icon-button"
                              aria-label="הצג מוצרים בקטגוריה"
                            >
                              <PackageSearch size={20} />
                            </button>
                            {children.length === 0 && (
                              <button
                                onClick={() =>
                                  handleDelete({
                                    id: getId(item),
                                    slug: item.slug,
                                  })
                                }
                                className="delete-button"
                              >
                                <FiTrash size={20} />
                              </button>
                            )}
                            <button
                              onClick={() => startRenamingCard(item)}
                              className="icon-button"
                            >
                              <FiEdit2 size={20} />
                            </button>
                            <button
                              type="button"
                              data-tip
                              data-for="movingCategory"
                              onClick={() => handleMoving(item)}
                              className="icon-button"
                            >
                              <Upload size={20} />
                            </button>
                            <button
                              type="button"
                              data-tip
                              data-for="showHeaderTip"
                              onClick={() => toggleShowHeader(item)}
                              className={`icon-button show-header-button${showsCategoryHeader(item) ? " show-header-button--on" : ""}`}
                              aria-label="הצגת שם קטגוריה"
                              aria-pressed={showsCategoryHeader(item)}
                            >
                              {showsCategoryHeader(item) ? (
                                <Eye size={20} />
                              ) : (
                                <EyeOff size={20} />
                              )}
                            </button>
                            <button
                              onClick={() => toggleActiveStatus(item)}
                              className="toggle-active-button"
                            >
                              {item.status === "ACTIVE" ? (
                                <FiCheckSquare size={20} />
                              ) : (
                                <FiSquare size={20} />
                              )}
                            </button>
                            {!isFamilyCategory(item) && level < 3 && (
                              <button
                                type="button"
                                data-tip
                                data-for="addSubCategoryTip"
                                onClick={() =>
                                  openAddingDialog(getId(item), item.slug)
                                }
                                className="add-category-button"
                                aria-label="הוספת תת קטגוריה"
                              >
                                <FiPlus size={20} />
                              </button>
                            )}
                          </span>
                        )}
                      </div>

                      {expandedCards[getId(item)] && children.length > 0 && (
                        <ShowCategory
                          parentId={getId(item)}
                          isNested={true}
                          level={level + 1}
                          categoriesState={children}
                          className="nested-categories"
                          searchVisibleIds={searchVisibleIds}
                          isMainSection={isMainSection}
                        />
                      )}
                    </div>
                  )}
                </Draggable>
              );
            })}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
      {addingDialogOpen && (
        <AddCategoryDialog
          handleAdding={addNewCategory}
          handleClose={setAddingDialogOpen}
          categoryLevel={fatherIdAddCategory === 0 ? 0 : level + 1}
        />
      )}
      {deleteDialogOpen && (
        <DeleteCategoryDialog
          handleDelete={deleteCtegoryWithProducts}
          handleClose={closeDeleteDialog}
        />
      )}
      {moveCategoryOpen && (
        <MoveCategoryDialog
          toMove={moveCategoryOpen}
          handleClose={setMoveCategoryOpen}
        />
      )}
    </DragDropContext>
  );
};

export default ShowCategory;
